import { Aggregation, BuiltInDimension } from '@fullstory/proto/fs/api/metrics';
import { DependentEventFilter, Event } from '@fullstory/proto/fs/api/segment';
import { Metric } from '@fullstory/proto/fs/services/search-settings';
import React, { PropsWithChildren, useContext, useMemo } from 'react';
import { PrimaryValue } from '../types';

export const appearancesPropertyMap = {
  text: BuiltInDimension.DIMENSION_TEXT,
  page: BuiltInDimension.DIMENSION_PAGE_DEF_ID,
  url: BuiltInDimension.DIMENSION_URL,
  path: BuiltInDimension.DIMENSION_URL_PATH,
  host: BuiltInDimension.DIMENSION_URL_HOST,
  queryParam: BuiltInDimension.DIMENSION_URL_QUERY,
  screen: BuiltInDimension.DIMENSION_APP_VIEW_NAME,
  device: BuiltInDimension.DIMENSION_DEVICE,
  element: BuiltInDimension.DIMENSION_CSS,
};

export type appearancesProperty = keyof typeof appearancesPropertyMap;

export type OnPreviewFn = (
  primary: PrimaryValue | null,
  refinements: DependentEventFilter[],
  allAsRefinements: DependentEventFilter[],
) => void;

export type OnPreviewFnWithIndex = (
  primary: PrimaryValue | null,
  refinements: DependentEventFilter[],
  allAsRefinements: DependentEventFilter[],
  index: number,
) => void;

export type MultiDimensionOverview = {
  overviewProperty: appearancesProperty;
  overviewSubProperty: appearancesProperty;
  propertyName: string;
  subPropertyName: string;
};

export enum DefaultMode {
  TOTAL_ONLY = 0,
  SCREENS = 1,
}

export interface AppearancesProps {
  event: Event;
  aggregation?: Aggregation;
  propertyOptions: appearancesProperty[];
  onPreview?: OnPreviewFn;
  objectType: string;
  multiDimensionOverview?: MultiDimensionOverview;
  supportsMobile?: boolean; // true by default
  forceDefaultMode?: DefaultMode;
  queryParams?: string[];
  // Styling override props
  noCollapse?: boolean; // if true, disables collapsible functionality and always shows expanded content
  hideVolumeSection?: boolean; // if true, hides the 7 day volume trends section
  pageSize?: number; // override the default number of items shown per page
  backgroundColor?: 'secondary' | 'default' | 'white' | 'neutral-10'; // defaults to 'secondary' if not provided
  noActivityBackgroundColor?: 'secondary' | 'default' | 'white' | 'neutral-10'; // defaults to undefined if not provided
  padding?: 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64; // defaults to 8 if not provided
}

export type AppearanceResult = { match: string; volume: number; id: string };

export type appearancesFn = (props: {
  event: Event | null;
  property: appearancesProperty;
  limit?: number;
}) => { matches: AppearanceResult[]; ready: boolean };

export type getMetricFn = (props: {
  event: Event | null;
  property?: appearancesProperty;
  computeNumGroups?: boolean;
  limit?: number;
}) => Metric | null;

export interface AppearancesContextProps {
  Appearances?: React.ComponentType<AppearancesProps>;
  showPreview: () => void;
  onPreview?: OnPreviewFnWithIndex;
  appearancesFn: appearancesFn;
  getMetricFn: getMetricFn;
}

export const AppearancesContext = React.createContext<AppearancesContextProps | null>(null);

export const AppearancesContextWithOnPreview: React.FC<
  PropsWithChildren<{ onPreview: OnPreviewFnWithIndex }>
> = ({ onPreview, children }) => {
  const appearances = useContext(AppearancesContext);
  const appearancesWithOnPreview: AppearancesContextProps | null = useMemo(() => {
    if (!appearances) {
      return null;
    }
    return {
      ...appearances,
      onPreview: (primary, refinements, allAsRefinements, index) => {
        appearances.onPreview?.(primary, refinements, allAsRefinements, index);
        onPreview(primary, refinements, allAsRefinements, index);
        appearances.showPreview();
      },
    };
  }, [appearances, onPreview]);

  return (
    <AppearancesContext.Provider value={appearancesWithOnPreview}>
      {children}
    </AppearancesContext.Provider>
  );
};
